// Registers HollowScript as a real Monaco language: tokeniser, live
// diagnostics, completion, hover, go to definition, document symbols and a
// "Format Document" action, all built on the pure logic in
// `src/engine/scripting/monaco/` (tokenisation, diagnostics, completion,
// hover, definition, outline, format - see that directory's own `index.ts` doc
// comment for why none of it imports `monaco-editor` itself, and for what runs
// on the native `tooling` build). This is the one module in the whole
// feature that is allowed to: it is the thin glue translating those plain
// data shapes into Monaco's actual provider interfaces, and it is only ever
// reached through `HollowScriptEditor.tsx`'s lazy `import()`, so the
// ~98MB `monaco-editor` package is still never pulled into the app's main
// bundle.
//
// Registration is global and idempotent (`registerHollowScriptLanguage`
// guards itself with a module-level promise): Monaco's language/theme/provider
// registries are page-lifetime singletons, not per-editor-instance state, so
// calling this more than once (every `<HollowScriptEditor>` mount calls it
// in `beforeMount`) must never register a second copy of anything - that
// would show every completion/hover result twice. It returns a promise because
// Monaco's tokeniser is synchronous but the native tooling wasm loads lazily:
// the tokens provider is registered once the wasm is ready, and an editor
// awaits that before it creates its model so the first paint is highlighted.
//
// A project's granted-capability set, by contrast, genuinely is per-editor
// (tier 1's Preview session vs tier 2's published play surface can differ),
// so it is threaded through a small module-level indirection
// (`setActiveHollowScriptCapabilities`) that the currently-mounted editor
// keeps up to date, rather than baked into the provider at registration
// time.
//
// So are the things that belong to one model rather than to the language: the
// script's own name (its import identity), the sibling scripts its `import`s
// resolve against, and which live native analysis handle it owns. An editor
// declares them with `bindHollowScriptModel`; the providers read them per
// request. A model's analysis handle is disposed when the model is
// (`analysisPool.ts` owns the rest of that lifecycle: one handle per model,
// disposed and re-made on change, a global ceiling under the module's own
// limit of 64).

import type * as Monaco from 'monaco-editor';
import type { StudioCapabilityName } from '../../../../engine/scripting/capabilityNames.js';
import type { NativeModules } from '../../../../engine/scripting/native/hollowscript.js';
import { getMonacoTooling, type MonacoTooling } from '../../../../engine/scripting/nativeTooling.js';
import {
  computeHollowScriptCompletions,
  computeHollowScriptDefinition,
  computeHollowScriptHover,
  computeHollowScriptMarkers,
  computeHollowScriptOutline,
  formatHollowScriptSource,
  releaseAnalysis,
  tokenizeHollowScriptLine,
  warmAnalysis,
  type AnalysisRequest,
  type HighlightClass,
  type HollowScriptCompletionKind,
  type OutlineItem,
  type OutlineItemKind,
} from '../../../../engine/scripting/monaco/index.js';
import { revealInScript } from '../editorCommands.js';

export const HOLLOWSCRIPT_LANGUAGE_ID = 'hollowscript';

export type GrantedCapabilitiesResolver = () => ReadonlySet<StudioCapabilityName> | undefined;

/** `undefined` grants every v1 capability - the same default posture
 * `narrowScriptCapabilities`/`createStudioCapabilities` already use
 * elsewhere for "not configured". */
let activeCapabilitiesResolver: GrantedCapabilitiesResolver = () => undefined;

/** Points the shared completion/hover providers at `resolver` for as long
 * as the calling editor is mounted. Returns a cleanup that restores whatever
 * resolver was active before it (correct for the common case of a single
 * live editor, and harmless in the rare case of a nested/sequential one).
 * Call from a `useEffect` keyed on the granted-capabilities value itself, not
 * just on mount - a creator unchecking a capability in Game Setup must reach
 * the very next keystroke's completion list, matching `DockConsole`'s own
 * live `allowedCapabilities` behaviour. */
export function setActiveHollowScriptCapabilities(resolver: GrantedCapabilitiesResolver): () => void {
  const previous = activeCapabilitiesResolver;
  activeCapabilitiesResolver = resolver;
  return () => {
    activeCapabilitiesResolver = previous;
  };
}

/** The URI scheme of a Code workspace script's Monaco model. */
export const HOLLOWSCRIPT_FILE_SCHEME = 'hollowscript-file';

/** The Monaco URI of the model that shows script `fileId`: also how a
 * go to definition into a sibling script names its target. */
export function hollowScriptFileUri(monaco: Pick<typeof Monaco, 'Uri'>, fileId: string): Monaco.Uri {
  return monaco.Uri.parse(`${HOLLOWSCRIPT_FILE_SCHEME}:///${encodeURIComponent(fileId)}.hws`);
}

/** The script id a `hollowScriptFileUri` names, or `undefined` for any other
 * resource. */
export function hollowScriptFileIdOf(resource: Pick<Monaco.Uri, 'scheme' | 'path'>): string | undefined {
  if (resource.scheme !== HOLLOWSCRIPT_FILE_SCHEME || !resource.path.endsWith('.hws')) return undefined;
  return decodeURIComponent(resource.path.slice(1, -'.hws'.length));
}

/** What belongs to one editor model rather than to the language. */
export interface HollowScriptModelContext {
  /** The script's own canonical path (`boot.hws`): its identity for
   * diagnostics and for which `import`s resolve to *other* modules. */
  readonly entryPath?: string;
  /** The sibling scripts an `import` resolves against, by canonical path
   * (`scriptModules.ts`'s `buildScriptModules`). */
  readonly modules?: NativeModules;
  /** The identity of this model's one live native analysis handle. Defaults
   * to the model's URI; the Code workspace passes one the Outline panel can
   * share (`hollowScriptAnalysisKey`). */
  readonly analysisKey?: string;
  /** The Monaco model URI of the script at canonical path `path`, so go to
   * definition can point into a sibling script. */
  readonly uriForModule?: (path: string) => Monaco.Uri | undefined;
}

/** The analysis key the Code workspace uses for script `fileId`, shared by
 * the editor model and the Outline panel so both read one handle. */
export function hollowScriptAnalysisKey(fileId: string): string {
  return `file:${fileId}`;
}

const WARM_DEBOUNCE_MS = 250;

interface ModelState {
  getContext: () => HollowScriptModelContext;
  warmTimer: ReturnType<typeof setTimeout> | undefined;
  contentListener: Monaco.IDisposable | undefined;
}

const modelStates = new WeakMap<Monaco.editor.ITextModel, ModelState>();
let anonymousModelCount = 0;
const anonymousKeys = new WeakMap<Monaco.editor.ITextModel, string>();

function stateFor(model: Monaco.editor.ITextModel): ModelState {
  let state = modelStates.get(model);
  if (state) return state;
  const created: ModelState = { getContext: () => ({}), warmTimer: undefined, contentListener: undefined };
  state = created;
  modelStates.set(model, state);
  // Whoever first asks about a model owns the promise to let go of its
  // analysis handle when the model goes, so a handle can never outlive it.
  model.onWillDispose(() => {
    if (created.warmTimer !== undefined) clearTimeout(created.warmTimer);
    created.contentListener?.dispose();
    releaseAnalysis(analysisKeyOf(model));
  });
  return state;
}

function analysisKeyOf(model: Monaco.editor.ITextModel): string {
  const explicit = modelStates.get(model)?.getContext().analysisKey;
  if (explicit !== undefined) return explicit;
  const uri = model.uri.toString();
  if (uri !== '') return uri;
  let key = anonymousKeys.get(model);
  if (key === undefined) {
    key = `model:${++anonymousModelCount}`;
    anonymousKeys.set(model, key);
  }
  return key;
}

/** The request every analysis-backed provider makes for `model` right now. */
function requestFor(model: Monaco.editor.ITextModel): AnalysisRequest {
  const context = stateFor(model).getContext();
  return {
    source: model.getValue(),
    entryPath: context.entryPath,
    modules: context.modules,
    key: analysisKeyOf(model),
    grantedCapabilities: activeCapabilitiesResolver(),
  };
}

/** Tells the providers what belongs to `model` (see `HollowScriptModelContext`;
 * `getContext` is read on every request, so it may return live values), and
 * keeps its native analysis warm: after a pause in typing, the model's one
 * handle is re-made for the new text, so the first hover or completion after
 * the pause answers from a handle that is already there. Hover and completion
 * never wait on this - a request for text the handle was not made from
 * re-analyses on demand - the debounce only keeps the common case instant.
 * Returns a cleanup that stops the warming and freezes the context at its
 * last value (the model, and its handle, outlive an editor widget). */
export function bindHollowScriptModel(model: Monaco.editor.ITextModel, getContext: () => HollowScriptModelContext): () => void {
  const state = stateFor(model);
  state.getContext = getContext;
  state.contentListener?.dispose();
  state.contentListener = model.onDidChangeContent(() => {
    if (state.warmTimer !== undefined) clearTimeout(state.warmTimer);
    state.warmTimer = setTimeout(() => {
      state.warmTimer = undefined;
      if (model.isDisposed()) return;
      warmAnalysis(requestFor(model)).catch(() => undefined);
    }, WARM_DEBOUNCE_MS);
  });
  return () => {
    if (state.warmTimer !== undefined) clearTimeout(state.warmTimer);
    state.warmTimer = undefined;
    state.contentListener?.dispose();
    state.contentListener = undefined;
    const frozen = getContext();
    state.getContext = () => frozen;
  };
}

const TOKEN_TYPE_FOR_CLASS: Readonly<Record<HighlightClass, string>> = {
  keyword: 'keyword.hollowscript',
  identifier: 'identifier.hollowscript',
  number: 'number.hollowscript',
  string: 'string.hollowscript',
  filter: 'regexp.hollowscript',
  operator: 'operator.hollowscript',
  punctuation: 'delimiter.hollowscript',
  comment: 'comment.hollowscript',
};

/** Tokenisation carries no real per-line state (`tokenize.ts`'s own doc
 * comment: every token is confined to one line), so `IState.equals` always
 * reporting `true` is correct, not a shortcut - it tells Monaco a line's
 * tokens never depend on what came before it, which is true here. */
class StatelessTokenizerState implements Monaco.languages.IState {
  clone(): Monaco.languages.IState {
    return this;
  }
  equals(): boolean {
    return true;
  }
}
const INITIAL_TOKENIZER_STATE = new StatelessTokenizerState();

function createTokensProvider(tooling: Pick<MonacoTooling, 'highlight'>): Monaco.languages.TokensProvider {
  return {
    getInitialState: () => INITIAL_TOKENIZER_STATE,
    tokenize: (line) => ({
      tokens: tokenizeHollowScriptLine(line, tooling).map((token) => ({
        startIndex: token.startIndex,
        scopes: token.kind ? TOKEN_TYPE_FOR_CLASS[token.kind] : '',
      })),
      endState: INITIAL_TOKENIZER_STATE,
    }),
  };
}

const LANGUAGE_CONFIGURATION: Monaco.languages.LanguageConfiguration = {
  // spec.md §1.1: `//` to end of line, no block comment form at all.
  comments: { lineComment: '//' },
  brackets: [
    ['{', '}'],
    ['[', ']'],
    ['(', ')'],
  ],
  autoClosingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: '"', close: '"', notIn: ['string', 'comment'] },
    // The filter literal delimiter (spec.md §1.5), HollowScript's embedded
    // foreign-language (HEL) literal - the same syntactic role a regexp
    // literal plays elsewhere, which is also why the tokeniser classifies it
    // as `regexp` (see `TOKEN_TYPE_FOR_CLASS`, mirroring Cascade's own
    // `hollowscript.ts` choice).
    { open: '`', close: '`', notIn: ['string', 'comment'] },
  ],
  surroundingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: '"', close: '"' },
    { open: '`', close: '`' },
  ],
};

function defineHollowScriptThemes(monaco: typeof Monaco): void {
  // Colours read from soulstack.css's own light/dark custom properties
  // (`--ss-bg`, `--ss-surface`, `--ss-accent`, `--ss-success`, `--ss-danger`,
  // `--ss-text-*`) so the editor reads as part of the surrounding panel
  // rather than a foreign widget dropped into it - Monaco themes need
  // literal colours, not CSS custom properties, so these are a one-time
  // snapshot of those values, not a live binding. The editor background is
  // deliberately the dockview panel background (`--ss-bg`), so there is no
  // visible seam where the editor ends and the panel begins.
  monaco.editor.defineTheme('hollowscript-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'keyword.hollowscript', foreground: 'a78bfa' }, // --ss-accent (dark)
      { token: 'string.hollowscript', foreground: '56b083' }, // --ss-success (dark)
      { token: 'number.hollowscript', foreground: 'e0a860' },
      { token: 'regexp.hollowscript', foreground: 'e0645d' }, // --ss-danger (dark), filter literals
      { token: 'comment.hollowscript', foreground: '76767c', fontStyle: 'italic' }, // --ss-text-3 (dark)
      { token: 'operator.hollowscript', foreground: 'b0b0b8' },
      { token: 'delimiter.hollowscript', foreground: '9a9aa2' },
      { token: 'identifier.hollowscript', foreground: 'e4e4e7' },
    ],
    colors: {
      'editor.background': '#1c1c1c', // --ss-bg (dark)
      'editor.foreground': '#f4f4f5', // --ss-text (dark)
      'editorGutter.background': '#1c1c1c',
      'editorLineNumber.foreground': '#4d4d52',
      'editorLineNumber.activeForeground': '#a6a6ac', // --ss-text-2
      'editor.lineHighlightBackground': '#ffffff08',
      'editor.lineHighlightBorder': '#00000000',
      'editor.selectionBackground': '#a78bfa40',
      'editor.inactiveSelectionBackground': '#a78bfa22',
      'editor.selectionHighlightBackground': '#a78bfa26',
      'editor.wordHighlightBackground': '#ffffff12',
      'editorCursor.foreground': '#c4b5fd', // --ss-accent-strong
      'editorIndentGuide.background1': '#ffffff10',
      'editorIndentGuide.activeBackground1': '#ffffff2a',
      'editorBracketMatch.background': '#a78bfa2a',
      'editorBracketMatch.border': '#a78bfa66',
      'editorWidget.background': '#242424', // --ss-surface
      'editorWidget.border': '#2e2e2e', // --ss-border
      'editorSuggestWidget.background': '#242424',
      'editorSuggestWidget.border': '#2e2e2e',
      'editorSuggestWidget.selectedBackground': '#8a5cf22e', // --ss-accent-soft
      'editorHoverWidget.background': '#242424',
      'editorHoverWidget.border': '#2e2e2e',
      'minimap.background': '#1c1c1c',
      'scrollbarSlider.background': '#ffffff14',
      'scrollbarSlider.hoverBackground': '#ffffff22',
      'scrollbarSlider.activeBackground': '#ffffff2e',
      'editorError.foreground': '#e0645d',
      'editorWarning.foreground': '#d8a54a',
      'focusBorder': '#a78bfa80',
    },
  });
  monaco.editor.defineTheme('hollowscript-light', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: 'keyword.hollowscript', foreground: '6444d6' }, // --ss-accent (light)
      { token: 'string.hollowscript', foreground: '2f9e6b' }, // --ss-success (light)
      { token: 'number.hollowscript', foreground: 'a3651b' },
      { token: 'regexp.hollowscript', foreground: 'd0453f' }, // --ss-danger (light)
      { token: 'comment.hollowscript', foreground: '8c8d97', fontStyle: 'italic' }, // --ss-text-3 (light)
      { token: 'operator.hollowscript', foreground: '5a5a63' },
      { token: 'delimiter.hollowscript', foreground: '6b6b74' },
      { token: 'identifier.hollowscript', foreground: '1a1b20' },
    ],
    colors: {
      'editor.background': '#eceff5', // --ss-bg (light)
      'editor.foreground': '#1a1b20',
      'editorGutter.background': '#eceff5',
      'editorLineNumber.foreground': '#b5b1a6',
      'editorLineNumber.activeForeground': '#565863', // --ss-text-2
      'editor.lineHighlightBackground': '#1a1b2008',
      'editor.lineHighlightBorder': '#00000000',
      'editor.selectionBackground': '#6444d62e',
      'editor.inactiveSelectionBackground': '#6444d618',
      'editor.selectionHighlightBackground': '#6444d61c',
      'editor.wordHighlightBackground': '#1a1b2010',
      'editorCursor.foreground': '#6444d6',
      'editorIndentGuide.background1': '#1a1b2012',
      'editorIndentGuide.activeBackground1': '#1a1b202c',
      'editorBracketMatch.background': '#6444d622',
      'editorBracketMatch.border': '#6444d666',
      'editorWidget.background': '#ffffff', // --ss-surface
      'editorWidget.border': '#1018301c', // --ss-border, rgba(16, 24, 48, 0.11)
      'editorSuggestWidget.background': '#ffffff',
      'editorSuggestWidget.border': '#1018301c',
      'editorSuggestWidget.selectedBackground': '#6444d61a',
      'editorHoverWidget.background': '#ffffff',
      'editorHoverWidget.border': '#1018301c',
      'minimap.background': '#eceff5',
      'scrollbarSlider.background': '#1a1b2018',
      'scrollbarSlider.hoverBackground': '#1a1b2028',
      'scrollbarSlider.activeBackground': '#1a1b2038',
      'editorError.foreground': '#d0453f',
      'editorWarning.foreground': '#c08a1e',
      'focusBorder': '#6444d680',
    },
  });
}

const COMPLETION_KIND_MAP: Readonly<Record<HollowScriptCompletionKind, number>> = {
  keyword: 17, // CompletionItemKind.Keyword
  capability: 8, // .Module
  namespace: 8, // .Module
  method: 0, // .Method
  function: 1, // .Function
  variable: 4, // .Variable
  constant: 14, // .Constant
  parameter: 4, // .Variable (Monaco has no distinct "parameter" kind)
  type: 5, // .Class
  enum: 15, // .Enum
};

function createCompletionProvider(): Monaco.languages.CompletionItemProvider {
  return {
    triggerCharacters: ['.'],
    async provideCompletionItems(model, position) {
      const word = model.getWordUntilPosition(position);
      const range: Monaco.IRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };
      const request = requestFor(model);
      const items = await computeHollowScriptCompletions({
        source: request.source,
        offset: model.getOffsetAt(position),
        lineTextBeforeCursor: model.getLineContent(position.lineNumber).slice(0, position.column - 1),
        grantedCapabilities: request.grantedCapabilities,
        entryPath: request.entryPath,
        modules: request.modules,
        key: request.key,
      });
      return {
        suggestions: items.map((item) => ({
          label: item.label,
          kind: COMPLETION_KIND_MAP[item.kind] as Monaco.languages.CompletionItemKind,
          detail: item.detail,
          documentation: item.documentation,
          insertText: item.insertText,
          sortText: item.sortText,
          range,
        })),
      };
    },
  };
}

function createHoverProvider(): Monaco.languages.HoverProvider {
  return {
    async provideHover(model, position) {
      const request = requestFor(model);
      const info = await computeHollowScriptHover({
        source: request.source,
        offset: model.getOffsetAt(position),
        grantedCapabilities: request.grantedCapabilities,
        entryPath: request.entryPath,
        modules: request.modules,
        key: request.key,
      });
      if (!info || model.isDisposed()) return null;
      const start = model.getPositionAt(info.start);
      const end = model.getPositionAt(info.end);
      return {
        range: { startLineNumber: start.lineNumber, startColumn: start.column, endLineNumber: end.lineNumber, endColumn: end.column },
        contents: info.markdownLines.map((value) => ({ value })),
      };
    },
  };
}

/** Go to definition (F12, Ctrl/Cmd-click): a name declared in this script
 * lands in this model; one brought in by `import` lands in the sibling
 * script's model, which the editor opener below then shows. */
function createDefinitionProvider(): Monaco.languages.DefinitionProvider {
  return {
    async provideDefinition(model, position) {
      const request = requestFor(model);
      const definition = await computeHollowScriptDefinition({
        source: request.source,
        offset: model.getOffsetAt(position),
        grantedCapabilities: request.grantedCapabilities,
        entryPath: request.entryPath,
        modules: request.modules,
        key: request.key,
      });
      if (!definition || model.isDisposed()) return null;
      const range: Monaco.IRange = {
        startLineNumber: definition.startLineNumber,
        startColumn: definition.startColumn,
        endLineNumber: definition.endLineNumber,
        endColumn: definition.endColumn,
      };
      if (definition.inEntry) return { uri: model.uri, range };
      const uri = stateFor(model).getContext().uriForModule?.(definition.file);
      return uri ? { uri, range } : null;
    },
  };
}

function createFormattingProvider(): Monaco.languages.DocumentFormattingEditProvider {
  return {
    displayName: 'HollowScript',
    // Monaco's `DocumentFormattingEditProvider` accepts a Promise here -
    // `formatHollowScriptSource` is async now (the native tooling wasm
    // loads lazily; see that function's own doc comment).
    async provideDocumentFormattingEdits(model) {
      const result = await formatHollowScriptSource(model.getValue());
      // A parse error means nothing to format - the same condition already
      // surfaces as a live diagnostic (`updateHollowScriptMarkers`), so a
      // silent no-op here (rather than a second error surface) is correct,
      // not a swallowed failure. Also the correct response if the model was
      // disposed while the (now async) format call was in flight - Monaco
      // itself discards a stale formatting result against a disposed model.
      if (result.formatted === undefined || model.isDisposed()) return [];
      return [{ range: model.getFullModelRange(), text: result.formatted }];
    },
  };
}

/** Monaco's `SymbolKind` values (monaco.languages.SymbolKind) as plain
 * numbers, matching how `COMPLETION_KIND_MAP` avoids a value import too. */
const SYMBOL_KIND_MAP: Readonly<Record<OutlineItemKind, number>> = {
  function: 11, // SymbolKind.Function
  record: 22, // .Struct
  enum: 9, // .Enum
  constant: 13, // .Constant
  variable: 12, // .Variable
  field: 7, // .Field
  enumMember: 21, // .EnumMember
  uses: 1, // .Module
  import: 1, // .Module
};

function toDocumentSymbol(item: OutlineItem): Monaco.languages.DocumentSymbol {
  return {
    name: item.name,
    detail: item.detail,
    kind: SYMBOL_KIND_MAP[item.kind] as Monaco.languages.SymbolKind,
    tags: [],
    range: { startLineNumber: item.line, startColumn: item.column, endLineNumber: item.endLine, endColumn: item.endColumn },
    selectionRange: { startLineNumber: item.selLine, startColumn: item.selColumn, endLineNumber: item.selEndLine, endColumn: item.selEndColumn },
    children: item.children.map(toDocumentSymbol),
  };
}

/** Powers Monaco's own "Go to Symbol" quick pick (Ctrl/Cmd+Shift+O) and the
 * breadcrumb bar with the same outline the Scripts panel shows, so both agree
 * on what a file declares. */
function createDocumentSymbolProvider(): Monaco.languages.DocumentSymbolProvider {
  return {
    displayName: 'HollowScript',
    async provideDocumentSymbols(model) {
      const request = requestFor(model);
      const items = await computeHollowScriptOutline({
        source: request.source,
        entryPath: request.entryPath,
        grantedCapabilities: request.grantedCapabilities,
        modules: request.modules,
        key: request.key,
      });
      return items.map(toDocumentSymbol);
    },
  };
}

/** Opens the script a go to definition names. Monaco's standalone editor can
 * only show a resource whose model is already attached to *an* editor, so
 * without this a Ctrl-click on an imported name would do nothing; this hands
 * the jump to the Code workspace, which opens that script and puts the caret
 * on the declaration (`revealInScript`). */
function createEditorOpener(): Monaco.editor.ICodeEditorOpener {
  return {
    openCodeEditor(_source, resource, selectionOrPosition) {
      const fileId = hollowScriptFileIdOf(resource);
      if (fileId === undefined) return false;
      const target = selectionOrPosition;
      const line = target === undefined ? 1 : 'lineNumber' in target ? target.lineNumber : target.startLineNumber;
      const column = target === undefined ? 1 : 'column' in target ? target.column : target.startColumn;
      revealInScript(fileId, line, column);
      return true;
    },
  };
}

let registration: Promise<void> | undefined;

/** Registers the `hollowscript` language, its tokeniser, both custom themes,
 * and the completion/hover/definition/format/symbol providers plus the
 * cross-script editor opener - exactly once, no matter how many times it is
 * called (see this module's own doc comment for why that matters). Call from
 * an editor's mount, and await it before creating the model: the tokeniser
 * can only be registered once the native tooling wasm has loaded. Everything
 * else is registered synchronously, before the first await. */
export function registerHollowScriptLanguage(monaco: typeof Monaco): Promise<void> {
  if (registration) return registration;

  monaco.languages.register({ id: HOLLOWSCRIPT_LANGUAGE_ID, extensions: ['.hws'], aliases: ['HollowScript', 'hollowscript'] });
  monaco.languages.setLanguageConfiguration(HOLLOWSCRIPT_LANGUAGE_ID, LANGUAGE_CONFIGURATION);
  defineHollowScriptThemes(monaco);
  monaco.languages.registerCompletionItemProvider(HOLLOWSCRIPT_LANGUAGE_ID, createCompletionProvider());
  monaco.languages.registerHoverProvider(HOLLOWSCRIPT_LANGUAGE_ID, createHoverProvider());
  monaco.languages.registerDefinitionProvider(HOLLOWSCRIPT_LANGUAGE_ID, createDefinitionProvider());
  monaco.languages.registerDocumentFormattingEditProvider(HOLLOWSCRIPT_LANGUAGE_ID, createFormattingProvider());
  monaco.languages.registerDocumentSymbolProvider(HOLLOWSCRIPT_LANGUAGE_ID, createDocumentSymbolProvider());
  monaco.editor.registerEditorOpener(createEditorOpener());

  registration = getMonacoTooling().then((tooling) => {
    monaco.languages.setTokensProvider(HOLLOWSCRIPT_LANGUAGE_ID, createTokensProvider(tooling));
  });
  return registration;
}

/** Per-model call counter, so an in-flight `computeHollowScriptMarkers` call
 * that resolves after a newer one (the native `check()` is async now - see
 * `updateHollowScriptMarkers`'s own doc comment) never overwrites its
 * successor's markers with stale ones. */
const markerCallGeneration = new WeakMap<Monaco.editor.ITextModel, number>();

/** Live inline diagnostics (`setModelMarkers`) against the native runtime's
 * `check()` - call on every model change, debounced by the caller
 * (`HollowScriptEditor.tsx`), the same discipline Cascade's own
 * `useHollowScriptCheck` debounce hook applies, for a related but not
 * identical reason now: `check` used to be synchronous with no real
 * in-flight call to cancel; it is asynchronous now (the native tooling
 * wasm loads lazily - `nativeTooling.ts`'s `getMonacoTooling`), so a
 * document change within the debounce window still should supersede, not
 * queue, the previous pending check, and now also must not let a slower,
 * older call's result land after a newer one's (`markerCallGeneration`
 * above guards exactly that). */
export async function updateHollowScriptMarkers(
  monaco: typeof Monaco,
  model: Monaco.editor.ITextModel,
  grantedCapabilities: ReadonlySet<StudioCapabilityName> | undefined,
  /** Defaults to the model's bound context (`bindHollowScriptModel`). */
  entryPath?: string,
  /** The sibling scripts `import`s resolve against; defaults to the model's
   * bound context, then to none. */
  modules?: NativeModules,
): Promise<void> {
  const generation = (markerCallGeneration.get(model) ?? 0) + 1;
  markerCallGeneration.set(model, generation);
  const context = modelStates.get(model)?.getContext();
  const markers = await computeHollowScriptMarkers(model.getValue(), grantedCapabilities, entryPath ?? context?.entryPath, {
    modules: modules ?? context?.modules,
  });
  if (model.isDisposed() || markerCallGeneration.get(model) !== generation) return;
  monaco.editor.setModelMarkers(
    model,
    HOLLOWSCRIPT_LANGUAGE_ID,
    markers.map((marker) => ({
      severity: marker.severity === 'error' ? 8 /* MarkerSeverity.Error */ : 4 /* MarkerSeverity.Warning */,
      message: marker.message,
      startLineNumber: marker.startLineNumber,
      startColumn: marker.startColumn,
      endLineNumber: marker.endLineNumber,
      endColumn: marker.endColumn,
    })),
  );
}
